import { Table } from "react-lib/frameworks/Table";
import { memo, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Form, FormField, FormGroup, Input, TextArea } from "semantic-ui-react";

// serializer
import ReceiptSerializer from "issara-sdk/types/ReceiptSerializer_apps_BIL";

import { DateTextBox } from "../common";
import {
  ACTIONS,
  BTN_ACTS,
  OnEvent,
  PaymentHistoryType,
  PickedState,
  RunSequence,
  SetProp,
} from "./sequence/PaymentHistory";
import { SelectedCustomerData } from "./types/MeBillingType";
import { STATUS_OPTIONS } from "../HISV3/BIL/CardBillingHistory";
import { flexTable } from "./CardPaymentSummary";

// Utils
import { formatComma } from "react-lib/utils/tsUtils";
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import ErrorMessage from "react-lib/component-js/common/ErrorMessage";
import ModInfo from "react-lib/appcon/common/ModInfo";
import ModConfirm from "react-lib/apps/common/cnmi/ModConfirm";
import { useIntl } from "react-intl";

type CardPaymentHistoryProps = {
  setProp: SetProp;
  runSequence: RunSequence;
  onEvent: OnEvent;
  // data
  PaymentHistorySequence?: PaymentHistoryType;
  selectedCustomer?: SelectedCustomerData;
  //
  languageUX: "en" | "th";
} & Pick<PickedState, "buttonLoadCheck" | "errorMessage" | "loadingStatus" | "masterOptions">;

type BillingHistoryFilterKey = keyof NonNullable<
  NonNullable<PaymentHistoryType>["billingHistoryFilter"]
>;

const CardPaymentHistory = (props: CardPaymentHistoryProps) => {
  const intl = useIntl();
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [openReceiptCopy, setOpenReceiptCopy] = useState<boolean>(false);
  const [note, setNote] = useState<string>("");
  const [copiedPrintSuccess, setCopiedPrintSuccess] = useState<boolean>(false);
  const [openCancelReceipt, setOpenCancelReceipt] = useState<boolean>(false);
  const [openModCancelReceipt, setOpenModCancelReceipt] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [cancelReceiptNote, setCancelReceiptNote] = useState<string>("");

  useEffect(() => {
    props.runSequence({
      sequence: "PaymentHistory",
      action: ACTIONS.SEARCH,
      restart: true,
    });
  }, []);

  const handleChangeDate = (type: "fromDate" | "toDate", date: string) => {
    props.setProp(`PaymentHistorySequence.billingHistoryFilter.${type}`, date);
  };

  const handleChangeData = (_event: any, data: any) => {
    let value = "";

    if (data.type === "checkbox") {
      value = data.checked;
    } else {
      value = data.value;
    }

    props.setProp(
      `PaymentHistorySequence.billingHistoryFilter.${data.name as BillingHistoryFilterKey}`,
      value,
    );
  };

  const flex = (justify: "center" | "right") => ({
    display: "flex",
    justifyContent: justify,
  });

  const paymentSummaryList = useMemo(
    () =>
      (props.PaymentHistorySequence?.billingHistory ?? []).map((item, index) => {
        const price = String(item.price).replace(/,/g, "");
        return {
          ...item,
          seq: <div style={flex("center")}>{index + 1}</div>,
          codeNumber: <div style={flex("center")}>{item.code}</div>,
          typeName: <div style={flex("center")}>{item.type_name}</div>,
          doctorName: <div>{item.doctor_name}</div>,
          createdDate: (
            <div style={flex("center")}>
              {formatISOtoBEDateTime(item?.created)?.dateTimeShortBracket}
            </div>
          ),
          priceNumber: <div style={flex("right")}>{formatComma(price)}</div>,
          statusLabel: <div style={flex("center")}>{item.status_label}</div>,
        };
      }),
    [props.PaymentHistorySequence?.billingHistory],
  );

  const handleSearch = () => {
    setSelectedRow({});

    props.runSequence({
      sequence: "PaymentHistory",
      action: ACTIONS.SEARCH,
    });
  };

  const handleRowProps = (state: any, rowInfo: any, column: any, instance: any) => ({
    style: {
      backgroundColor: selectedRow && rowInfo?.original?.id === selectedRow ? "#cccccc" : "white",
    },
    onClick: async () => {
      setSelectedRow(rowInfo?.original?.id);

      props.runSequence({
        sequence: "PaymentHistory",
        action: ACTIONS.GET_RECEIPT,
        selectData: rowInfo?.original,
      });
    },
  });

  const invoiceItemData = useMemo(() => {
    return (props.PaymentHistorySequence?.invoiceItem ?? []).map((item, index) => {
      return {
        ...item,
        code: flexTable("center")(item?.code),
        modeName: flexTable()(item?.product),
        createdDate: flexTable()(formatISOtoBEDateTime(item?.created)?.dateTimeShortBracket),
        quantity: flexTable()(formatComma(String(item?.quantity ?? 0))),
        unitprice: flexTable("flex-end")(formatComma(String(item?.unitprice ?? 0))),
        price: flexTable("flex-end")(formatComma(String(item?.price ?? 0))),
        payable: flexTable("flex-end")(formatComma(String(item?.payable ?? 0))),
      };
    });
  }, [props.PaymentHistorySequence?.invoiceItem]);

  const handleCloseReceiptCopy = () => {
    props.setProp("errorMessage", {
      ...props.errorMessage,
      [`${ACTIONS.PRINT_RECEIPT_COPPY}`]: null,
    });

    setOpenReceiptCopy(false);
    setNote("");
  };

  const handlePrintReceipt = (e: any, data: any) => {
    props.runSequence({
      sequence: "PaymentHistory",
      action: ACTIONS.PRINT_RECEIPT,
    });
  };

  const handlePrintReceiptCopy = (e: any, data: any) => {
    props.runSequence({
      sequence: "PaymentHistory",
      action: ACTIONS.PRINT_RECEIPT_COPPY,
      note: note,
      callback: (receiptCopy: boolean, note: string) => {
        setOpenReceiptCopy(receiptCopy);
        setNote(note);
        setCopiedPrintSuccess(!receiptCopy);
      },
    });
  };

  const handleCancelReceipt = () => {
    props.runSequence({
      sequence: "PaymentHistory",
      action: ACTIONS.CANCEL_RECEIPT,
      username: username,
      password: password,
      note: cancelReceiptNote,
      callback: (modType: string, isClose: boolean) => {
        setOpenModCancelReceipt(modType);
        setOpenCancelReceipt(isClose);
      },
    });
  };

  const handleCloseCancelReceipt = () => {
    props.setProp("errorMessage", {
      ...props.errorMessage,
      [`${ACTIONS.CANCEL_RECEIPT}`]: null,
    });

    setUsername("");
    setPassword("");
    setCancelReceiptNote("");
    setOpenCancelReceipt(false);
  };

  return (
    <div style={{ padding: "1rem" }}>
      <Form>
        <FormGroup inline>
          <FormField style={{ minWidth: "max-content", fontSize: "16px" }}>
            <label>ประวัติการชำระค่าบริการ</label>
          </FormField>
          <FormField style={{ minWidth: "max-content" }}>
            <label>รหัสเอกสาร</label>
          </FormField>
          <FormField width={2}>
            <Input
              fluid
              name="subInvoiceCode"
              value={props.PaymentHistorySequence?.billingHistoryFilter?.subInvoiceCode}
              onChange={handleChangeData}
            />
          </FormField>
          <FormField style={{ minWidth: "max-content" }}>
            <label>สถานะ</label>
          </FormField>
          <FormField width={2}>
            <div style={{ width: "100%" }}>
              <Dropdown
                fluid
                selection
                clearable
                name="status"
                options={STATUS_OPTIONS}
                value={props.PaymentHistorySequence?.billingHistoryFilter?.status}
                onChange={handleChangeData}
              />
            </div>
          </FormField>
          <FormField style={{ minWidth: "max-content" }}>
            <label>วันที่ออกเอกสาร</label>
          </FormField>
          <FormField width={3}>
            <DateTextBox
              style={{ width: "100%" }}
              value={props.PaymentHistorySequence?.billingHistoryFilter?.fromDate}
              onChange={(date: string) => handleChangeDate("fromDate", date)}
            />
          </FormField>
          <FormField>
            <label>-</label>
          </FormField>
          <FormField width={3}>
            <DateTextBox
              style={{ width: "100%" }}
              value={props.PaymentHistorySequence?.billingHistoryFilter?.toDate}
              onChange={(date: string) => handleChangeDate("toDate", date)}
            />
          </FormField>
          <FormField>
            <div style={{ width: "100%" }}>
              <ButtonLoadCheck
                // function
                setProp={props.setProp}
                onClick={handleSearch}
                // data
                paramKey={BTN_ACTS.SEARCH}
                buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
                // config
                style={{ background: "var(--primary-theme-color)", color: "white" }}
                fluid
                size="small"
                title={"ค้นหา"}
              />
            </div>
          </FormField>
        </FormGroup>
      </Form>

      <Table
        className="--rt-tbody-overflow-y-hidden"
        data={paymentSummaryList}
        getTrProps={handleRowProps}
        headers="ลำดับ, รหัสเอกสาร, ประเภทเอกสาร, ผู้ให้บริการ, วันที่เวลาออกเอกสาร, มูลค่า, สถานะเอกสาร"
        keys="seq, codeNumber, typeName, doctorName, createdDate, priceNumber, statusLabel"
        showPagination={false}
        style={{ height: "50vh" }}
        widths="80, 100, 120, ^150, ^150, ^120, ^120"
      />

      <div
        style={{
          marginTop: "20px",
          ...(!props.PaymentHistorySequence?.selecteBillingHistory?.id && { display: "none" }),
        }}
      >
        <Form>
          <FormGroup
            className="--override-disabled --grey"
            inline
            style={{ display: "flex", alignItems: "flex-end" }}
          >
            <FormField width={4} style={{ marginBottom: "5px" }}>
              <label style={{ color: "var(--primary-theme-color)", fontSize: "16px" }}>
                ข้อมูลใบเสร็จรับเงิน
              </label>
            </FormField>
            <FormField width={3} style={{ display: "block" }}>
              <label>เลขที่ใบเสร็จ</label>
              <Input
                fluid
                disabled
                value={props.PaymentHistorySequence?.selecteBillingHistory?.code}
              />
            </FormField>
            <FormField style={{ display: "block", minWidth: "max-content" }}>
              <label>หมายเลขรับบริการ</label>
              <Input
                fluid
                disabled
                value={props.PaymentHistorySequence?.selecteBillingHistory?.encounter_no}
              />
            </FormField>
            <FormField width={3} style={{ display: "block" }}>
              <label>สถานที่รับบริการ</label>
              <Input
                fluid
                disabled
                value={props.PaymentHistorySequence?.selecteBillingHistory?.division_name}
              />
            </FormField>
            <FormField width={3} style={{ display: "block" }}>
              <label>วันที่เวลา ออกเอกสาร</label>
              <Input
                fluid
                disabled
                value={
                  formatISOtoBEDateTime(
                    props.PaymentHistorySequence?.selecteBillingHistory?.created,
                  )?.dateTimeShortBracket
                }
              />
            </FormField>
            <FormField style={{ display: "block", minWidth: "max-content" }}>
              <label>สถานะเอกสาร</label>
              <Input
                fluid
                disabled
                value={props.PaymentHistorySequence?.selecteBillingHistory?.status_label}
              />
            </FormField>
          </FormGroup>
        </Form>

        <Table
          className="--rt-tbody-overflow-y-hidden"
          data={invoiceItemData}
          headers="รหัส, รายการค่าบริการ, วันที่เวลาค่าใช้จ่าย, จำนวน, ราคาต่อหน่วย, ราคารวม, ยอดชำระ"
          keys="code, modeName, createdDate, quantity, unitprice, price, payable"
          showPagination={false}
          style={{ height: "50vh" }}
          widths="^60,auto,^80,^50,^60,^60,^60"
        />

        <div
          className="ui form"
          style={{
            display: "flex",
            padding: "5px 10px",
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ margin: "0px 50px" }}>
            <label>ยอดรวมทั้งหมด</label>
          </div>
          <div className="--override-disabled --grey">
            <Input
              disabled={true}
              value={props.PaymentHistorySequence?.selecteBillingHistory?.price || "0.00"}
            ></Input>
          </div>
          <div style={{ flex: 1 }}></div>
          <div>
            <ButtonLoadCheck
              // function
              setProp={props.setProp}
              onClick={handlePrintReceipt}
              // data
              size="mini"
              color="green"
              paramKey={BTN_ACTS.PRINT_RECEIPT}
              buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.PRINT_RECEIPT]}
              disabled={
                props.PaymentHistorySequence?.selecteBillingHistory?.status_name === "CANCELED"
              }
              // config
              title={"พิมพ์ใบเสร็จต้นฉบับ"}
            />
          </div>
          <div>
            <Button
              onClick={() => setOpenReceiptCopy(true)}
              size="mini"
              style={{ background: "var(--primary-theme-color)", color: "white" }}
              disabled={
                props.PaymentHistorySequence?.selecteBillingHistory?.status_name === "CANCELED"
              }
            >
              {"พิมพ์สำเนาใบเสร็จ"}
            </Button>
          </div>
          <div>
            <Button
              onClick={() => setOpenCancelReceipt(true)}
              size="mini"
              color="red"
              disabled={
                props.PaymentHistorySequence?.selecteBillingHistory?.status_name === "CANCELED"
              }
            >
              {"ยกเลิกใบเสร็จ"}
            </Button>
          </div>
        </div>
      </div>

      <ModConfirm
        openModal={openReceiptCopy}
        onCloseWithDimmerClick={handleCloseReceiptCopy}
        titleName={intl.formatMessage({ id: "ระบุเหตุผลการพิมพ์สำเนาใบเสร็จ" })}
        size="tiny"
        backgroundColor={"var(--primary-theme-color)"}
        content={
          <div>
            <ErrorMessage error={props.errorMessage?.[`${ACTIONS.PRINT_RECEIPT_COPPY}`]} />
            <div style={{ fontWeight: "bold", marginBottom: "10px" }}>
              ระบุเหตุผลการพิมพ์สำเนาใบเสร็จ<span style={{ color: "red" }}>*</span>
            </div>
            <Form>
              <TextArea
                rows={4}
                onChange={(e) => setNote(e.target.value)}
                style={{ resize: "none", width: "100%" }}
                value={note}
              ></TextArea>
            </Form>
          </div>
        }
        approveButton={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handlePrintReceiptCopy}
            // data
            paramKey={BTN_ACTS.PRINT_RECEIPT_COPPY}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.PRINT_RECEIPT_COPPY]}
            // config
            style={{ background: "var(--primary-theme-color)", color: "white" }}
            fluid
            title={"ยืนยัน"}
          />
        }
        denyButton={
          <Button
            onClick={handleCloseReceiptCopy}
            style={{ background: "#a7a7a7", color: "white" }}
            fluid
          >
            {"ปิดหน้าต่าง"}
          </Button>
        }
      />

      <ModInfo
        type="success"
        titleColor={"green"}
        backgroundColor={"var(--primary-theme-color)"}
        titleName={intl.formatMessage({ id: "แจ้งเตือน" })}
        closeOnDimmerClick
        open={copiedPrintSuccess}
        onApprove={() => {
          props.setProp(`buttonLoadCheck.${BTN_ACTS.PRINT_RECEIPT_COPPY}`, null);
          setCopiedPrintSuccess(false);
        }}
        alertText={
          <div style={{ fontSize: "16px", fontWeight: "bold" }}>{"พิมพ์สำเนาใบเสร็จสำเร็จ"}</div>
        }
        onClose={() => setCopiedPrintSuccess(false)}
      />

      <ModInfo
        type="success"
        size="tiny"
        titleColor={"blue"}
        titleName={intl.formatMessage({ id: "แจ้งเตือน" })}
        closeOnDimmerClick
        open={props.PaymentHistorySequence?.openModPrintCoverageDocumentError}
        alertText="ผู้ป่วยไม่มีค่าใช้จ่ายที่ต้องพิมพ์เอกสารส่งเบิกตามสิทธิรักษา"
        onApprove={() => {
          props.setProp("PaymentHistorySequence.openModPrintCoverageDocumentError", false);
        }}
        onClose={() =>
          props.setProp("PaymentHistorySequence.openModPrintCoverageDocumentError", false)
        }
      />

      <ModConfirm
        closeIcon={true}
        backgroundColor={"var(--primary-theme-color)"}
        openModal={openCancelReceipt}
        titleName={intl.formatMessage({ id: "ระบุเหตุผลการยกเลิกใบเสร็จ" })}
        content={
          <div style={{ fontWeight: "bold" }}>
            <ErrorMessage error={props.errorMessage?.[ACTIONS.CANCEL_RECEIPT]} />
            <div>Username</div>
            <div style={{ width: "100%" }}>
              <Input
                placeholder="Username"
                value={username}
                onChange={(e: any, { value }: any) => setUsername(value)}
                style={{ width: "100%", marginBottom: "15px" }}
              />
            </div>
            <div>Password</div>
            <div style={{ width: "100%" }}>
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e: any, { value }: any) => setPassword(value)}
                style={{ width: "100%", marginBottom: "15px" }}
              />
            </div>
            <div>{intl.formatMessage({ id: "เหตุผลการยกเลิกใบเสร็จ" })}</div>
            <div style={{ width: "100%" }}>
              <Dropdown
                selection
                options={props.masterOptions?.cancel_receipt}
                value={cancelReceiptNote}
                onChange={(e: any, data: any) => {
                  setCancelReceiptNote(data.value);
                }}
                style={{ width: "100%", marginBottom: "15px" }}
              />
            </div>
          </div>
        }
        onCloseWithDimmerClick={handleCloseCancelReceipt}
        approveButton={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleCancelReceipt}
            // data
            paramKey={BTN_ACTS.CANCEL_RECEIPT}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.CANCEL_RECEIPT]}
            // config
            style={{ background: "var(--primary-theme-color)", color: "white" }}
            fluid
            title={"ใช่"}
          />
        }
        denyButton={
          <Button
            onClick={handleCloseCancelReceipt}
            style={{ background: "#a7a7a7", color: "white" }}
            fluid
          >
            {"ไม่"}
          </Button>
        }
      />

      <ModInfo
        type="success"
        titleName={intl.formatMessage({ id: "ยกเลิกใบเสร็จ สำเร็จ" })}
        closeOnDimmerClick
        open={openModCancelReceipt === "success"}
        onApprove={() => {
          setOpenModCancelReceipt("");
        }}
        onClose={() => setOpenModCancelReceipt("")}
      />
    </div>
  );
};

CardPaymentHistory.displayName = "CardPaymentHistory";

export default memo(CardPaymentHistory);
